import React from 'react';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { Transaction } from 'components/TransactionAllocator';
import TransactionAllocator from 'components/TransactionAllocator';
import CurrencyRate from 'components/CurrencyRate';
import { CalendarDay } from 'further-ui/utils';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import { ActionRef, ActionTypes } from 'further-types/lp/action';
import useActionTransactions from 'hooks/lp/ui/transactions/useActionTransactions';
import buildRealisationTransactions from './transactions/buildTransactions';
import Spacing from 'components/Spacing';
import {
  HoldingClassInvestmentsConsiderationValues,
  HoldingClassRealisations,
} from '../types';

type ReviewAccountTransactionsProps = {
  selectedVehicle: Vehicle;
  currency: CurrencyCode;
  currencyForTcyDisplay: CurrencyCode;
  ledgerDate: CalendarDay;
  fxRate: CurrencyRateDetails | null;
  changeRate: (rateDetails: CurrencyRateDetails) => void;
  isFetchingRate: boolean;
  transactionTypeOptions: Array<{
    label: string;
    value: string | null;
  }>;
  isThreeCyFxRateRealisation: boolean;
  threeCyFxRate: CurrencyRateDetails | null;
  threeCyChangeRate: (rateDetails: CurrencyRateDetails) => void;
  threeCyIsFetchingRate: boolean;
  onChangeTransaction: (transactions: Array<Transaction>) => void;
  unrealisedGainsUnwindPerHoldingClassInvestment: Array<{
    holdingClassId: string;
    investmentId: string;
    investmentDate: CalendarDay | undefined;
    unrealisedGainsUnwindAtDateLcy: number;
    unrealisedGainsUnwindAtDateTcy: number;
  }>;
  unrealisedAssetFXGainsAndLossesUnwinds: Array<{
    investmentId: string;
    holdingClassId: string;
    unrealisedAssetFXGainAmountToUnwindLcy: number;
    transactions: Array<
      Transaction & {
        isUnrealisedAssetFxGainOrLossUnwind: boolean;
        investmentId: string;
        holdingClassId: string;
      }
    >;
  }>;
  saleOfInvestmentData: Array<{
    investmentId: string;
    holdingClassId: string;
    investmentDate: CalendarDay;
    investmentSaleUnwindLcy: number;
    investmentSaleUnwindTcy: number;
  }>;
  holdingClassRealisation: HoldingClassRealisations;
  holdingClassInvestmentConsiderationValues: Array<HoldingClassInvestmentsConsiderationValues>;
  holdingClassCurrency: CurrencyCode;
};

const ReviewAccountTransactions: React.FC<ReviewAccountTransactionsProps> = ({
  selectedVehicle,
  currency,
  currencyForTcyDisplay,
  ledgerDate,
  fxRate,
  changeRate,
  isFetchingRate,
  transactionTypeOptions,
  onChangeTransaction,
  unrealisedGainsUnwindPerHoldingClassInvestment,
  unrealisedAssetFXGainsAndLossesUnwinds,
  saleOfInvestmentData,
  holdingClassRealisation,
  holdingClassInvestmentConsiderationValues,
  threeCyIsFetchingRate,
  threeCyFxRate,
  threeCyChangeRate,
  isThreeCyFxRateRealisation,
  holdingClassCurrency,
}) => {
  const { watch } = useFormContext<rules.CreateRealisationFormValues>();

  const transactions = watch('transactions');

  const forceShowTcy =
    currencyForTcyDisplay !== currency &&
    currencyForTcyDisplay !== selectedVehicle.baseCurrency;

  const defaultActionTransactions = useActionTransactions({
    vehicleId: selectedVehicle._id,
    action: {
      actionRef: ActionRef.Realisation,
      actionType: ActionTypes.RealisationCreated,
      amountIncVatTcy: 0,
      amountExVatTcy: 0,
      currency,
    },
    transactions,
  });

  const finalTransactions = [
    ...buildRealisationTransactions({
      defaultActionTransactions,
      holdingClassRealisation,
      realisationCurrencyRate: fxRate,
      threeCyRealisationCurrencyRate: threeCyFxRate,
      unrealisedGainsUnwindPerHoldingClassInvestment,
      holdingClassInvestmentsConsiderationValues:
        holdingClassInvestmentConsiderationValues,
      saleOfInvestment: saleOfInvestmentData,
      realisationCurrencyCode: currency,
      vehicleCurrencyCode: selectedVehicle.baseCurrency,
    }),
    ...unrealisedAssetFXGainsAndLossesUnwinds.flatMap(
      (unwind) => unwind.transactions,
    ),
  ].filter(
    (t) =>
      t.amountExVatTcy !== 0 ||
      (t.amountExVatLcy !== 0 && t.amountExVatLcy !== undefined),
  );

  return (
    <Spacing>
      {isThreeCyFxRateRealisation && (
        <CurrencyRate
          currency={holdingClassCurrency}
          date={ledgerDate}
          vehicle={selectedVehicle}
          fxRate={threeCyFxRate}
          onRateChange={threeCyChangeRate}
          isFetchingRate={threeCyIsFetchingRate}
        />
      )}
      <CurrencyRate
        currency={currencyForTcyDisplay}
        date={ledgerDate}
        vehicle={selectedVehicle}
        fxRate={fxRate}
        onRateChange={changeRate}
        isFetchingRate={isFetchingRate}
        forceShow={forceShowTcy}
      />
      <TransactionAllocator
        vehicle={selectedVehicle}
        title="Account transactions (mega-set)"
        transactions={finalTransactions}
        transactionTypeOptions={transactionTypeOptions}
        onChange={onChangeTransaction}
        transactionCurrency={currencyForTcyDisplay}
        fxRate={fxRate}
        forceShowTcy={forceShowTcy}
      />
    </Spacing>
  );
};

export default ReviewAccountTransactions;
